import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  Checkbox,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
} from '@mui/material';
import axios from 'axios';
import DownloadIcon from '@mui/icons-material/Download';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import CropOriginalIcon from '@mui/icons-material/CropOriginal';
import BrushIcon from '@mui/icons-material/Brush';
import { API_BASE_URL } from '../config';
import { ITemplate } from '../types/damTypes';
import { formatAssetReferenceLabels } from '../modules/referenceFields/utils';
import {
  sanitizeSimpleTemplateHtml,
  shouldRenderSimpleTemplateHtml,
} from '../utils/templateFieldHtml';

type PublicCollectionResponse =
  | {
      requiresPassword: true;
      collectionName: string;
      customMessage?: string;
      banner?: {
        enabled?: boolean;
        imagePath?: string;
        height?: number;
        focalY?: number;
      };
    }
  | {
      requiresPassword: false;
      collection: {
        _id: string;
        name: string;
        description?: string;
        assets: string[];
      };
      allowDownload: boolean;
      settings?: {
        customMessage?: string;
        watermark?: boolean;
        includeMetadata?: boolean;
        banner?: {
          enabled?: boolean;
          imagePath?: string;
          height?: number;
          focalY?: number;
        };
      };
    };

interface PublicSharePageProps {
  shareToken: string;
}

function publicMediumSrc(asset: PublicAsset | null): string | null {
  const medium = asset?.previewPaths?.medium;
  if (!medium || !medium.startsWith('/uploads/medium/') || medium.includes('..')) return null;
  return `${API_BASE_URL}${medium}`;
}

interface PublicAsset {
  _id: string;
  fileName: string;
  filePath?: string;
  fileType: string;
  fileSize: number;
  categories?: string[];
  categoryDetails?: PublicCategorySummary[];
  previewPaths?: {
    thumbnail?: string;
    medium?: string;
  };
  description?: string;
  metadata?: Record<string, any>;
  template?: {
    _id: string;
    name: string;
    fields: Array<{
      id: string;
      name: string;
      label: string;
      type: string;
      order: number;
      showDuringEdit?: boolean;
    }>;
  };
}

interface PublicCategorySummary {
  _id: string;
  name: string;
  parentCategory?: string | null;
  pathNames?: string[];
}

type PublicMetadataProfile = 'products' | 'wine' | 'recipes';
type PublicAssetFilter = 'all' | 'products' | 'wine' | 'recipes' | 'logo' | 'environment';

const PUBLIC_ASSET_FILTERS: Array<{
  id: Exclude<PublicAssetFilter, 'all'>;
  label: string;
  exactAliases: string[];
  containsAliases?: string[];
}> = [
  { id: 'products', label: 'Produkt', exactAliases: ['produkt', 'produkter', 'product', 'products'] },
  { id: 'wine', label: 'Vin', exactAliases: ['vin', 'viner', 'wine', 'wines'] },
  { id: 'recipes', label: 'Recept', exactAliases: ['recept', 'recipe', 'recipes'] },
  { id: 'logo', label: 'Logo', exactAliases: ['logo', 'logotyp'], containsAliases: ['logotyp'] },
  { id: 'environment', label: 'Miljö', exactAliases: ['miljo', 'miljobild', 'environment'], containsAliases: ['miljobild', 'environment'] },
];

const PUBLIC_METADATA_FIELD_PROFILES: Record<PublicMetadataProfile, string[]> = {
  products: [
    'produktnamn',
    'productname',
    'validooproductname',
    'artikelnummer',
    'artnr',
    'artnummer',
    'articlenumber',
    'nettovikt',
    'vikt',
    'netcontent',
    'netweight',
    'nettoinnehall',
    'validoonetcontent',
    'avrunnenvikt',
    'drainedweight',
    'ursprungsland',
    'countryoforigin',
    'countryorigin',
    'origin',
    'produktbeskrivning',
    'beskrivning',
    'description',
    'marketingmessage',
    'validoomarketingmessage',
    'eankod',
    'eankodfrp',
    'ean',
    'gtin',
    'gtinkod',
    'barcode',
    'naringsvardeper100g',
    'naringsvardenper100g',
    'naringsvarde',
    'nutritionalclaim',
    'nutritionalinfo',
    'nutrition',
    'innehallsdeklaration',
    'ingrediensforteckning',
    'ingredientstatement',
    'ingredients',
    'ingredienser',
  ],
  wine: [
    'produktnamn',
    'produktnamnvinid',
    'artikelnummer',
    'artikelnummervinid',
    'artnr',
    'articlenumber',
    'producent',
    'producentvinid',
    'producer',
    'volym',
    'volymvinid',
    'volume',
    'beskrivning',
    'description',
    'ursprungsland',
    'countryoforigin',
    'beskrivningvingard',
    'beskrivningvingardvinid',
    'vingard',
    'vingardvinid',
    'vineyard',
    'bestallningsnummersystembolaget',
    'bestallningsnummersystembolagetvinid',
    'systembolagetbestallningsnummer',
    'systembolaget',
  ],
  recipes: [
    'receptnamn',
    'receptnamnreceptid',
    'recipename',
    'beskrivning',
    'description',
    'tillagningstid',
    'tillagningstidreceptid',
    'cookingtime',
    'cooktime',
    'antalportioner',
    'antalportionerreceptid',
    'portioner',
    'servings',
    'ingredienser',
    'ingredients',
    'instruktioner',
    'instruktionerreceptid',
    'instructions',
  ],
};

const normalizePublicShareText = (value?: string | null): string => (
  (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '')
);

const profileTokenMatches = (token: string, target: string): boolean => (
  token === target || token.includes(target)
);

const categoryTokensMatchAliases = (
  tokens: string[],
  exactAliases: string[],
  containsAliases: string[] = [],
): boolean => (
  tokens.some((token) => (
    exactAliases.includes(token) || containsAliases.some((alias) => token.includes(alias))
  ))
);

const getAssetCategoryTokens = (asset: PublicAsset): string[] => (
  (asset.categoryDetails || [])
    .flatMap((category) => [category.name, ...(category.pathNames || [])])
    .map((value) => normalizePublicShareText(value))
    .filter(Boolean)
);

const assetMatchesFilterDefinition = (
  asset: PublicAsset,
  filter: Exclude<PublicAssetFilter, 'all'>,
): boolean => {
  const filterDef = PUBLIC_ASSET_FILTERS.find((candidate) => candidate.id === filter);
  if (!filterDef) return false;

  return categoryTokensMatchAliases(
    getAssetCategoryTokens(asset),
    filterDef.exactAliases,
    filterDef.containsAliases,
  );
};

const getMetadataProfileForAsset = (asset: PublicAsset, template?: any): PublicMetadataProfile | null => {
  if (assetMatchesFilterDefinition(asset, 'recipes')) return 'recipes';
  if (assetMatchesFilterDefinition(asset, 'wine')) return 'wine';
  if (assetMatchesFilterDefinition(asset, 'products')) return 'products';

  const templateToken = normalizePublicShareText(template?.name);
  if (profileTokenMatches(templateToken, 'recept')) return 'recipes';
  if (templateToken === 'vin' || templateToken === 'vinid' || templateToken === 'wine') return 'wine';
  if (profileTokenMatches(templateToken, 'produkt')) return 'products';
  return null;
};

const getProfileChipLabel = (profile: PublicMetadataProfile | null): string | null => {
  if (profile === 'products') return 'Produkt';
  if (profile === 'wine') return 'Vin';
  if (profile === 'recipes') return 'Recept';
  return null;
};

const fieldTextMatchesAlias = (fieldText: string, alias: string): boolean => {
  if (!fieldText || !alias) return false;
  if (fieldText === alias) return true;

  // Short aliases like "vikt" and "ean" should only match exact fields to avoid
  // leaking broader fields such as bruttovikt.
  if (alias.length <= 4) return false;
  return fieldText.includes(alias) || (fieldText.length > 4 && alias.includes(fieldText));
};

const isFieldAllowedForProfile = (
  field: { id?: string; name?: string; label?: string },
  profile: PublicMetadataProfile | null,
): boolean => {
  if (!profile) return false;
  const aliases = PUBLIC_METADATA_FIELD_PROFILES[profile];
  const checks = [field.id, field.name, field.label]
    .map((value) => normalizePublicShareText(value))
    .filter(Boolean);

  return checks.some((fieldText) => aliases.some((alias) => fieldTextMatchesAlias(fieldText, alias)));
};

const PUBLIC_METADATA_TITLE_ALIASES: Record<PublicMetadataProfile, string[]> = {
  products: ['produktnamn', 'productname', 'validooproductname'],
  wine: ['produktnamn', 'produktnamnvinid'],
  recipes: ['receptnamn', 'receptnamnreceptid', 'recipename'],
};

const PUBLIC_METADATA_SECTION_ALIASES: Record<PublicMetadataProfile, Array<{ id: string; title: string; aliases: string[] }>> = {
  products: [
    {
      id: 'product-basics',
      title: 'Produktinformation',
      aliases: [
        'produktnamn',
        'productname',
        'artikelnummer',
        'artnr',
        'nettovikt',
        'netcontent',
        'netweight',
        'avrunnenvikt',
        'drainedweight',
        'ursprungsland',
        'countryoforigin',
        'eankod',
        'eankodfrp',
        'ean',
        'gtin',
        'gtinkod',
        'barcode',
        'vikt',
      ],
    },
    {
      id: 'product-content',
      title: 'Beskrivning & innehåll',
      aliases: [
        'produktbeskrivning',
        'beskrivning',
        'description',
        'marketingmessage',
        'innehallsdeklaration',
        'ingrediensforteckning',
        'ingredientstatement',
        'ingredients',
        'ingredienser',
      ],
    },
    {
      id: 'product-nutrition',
      title: 'Näringsvärde',
      aliases: [
        'naringsvardeper100g',
        'naringsvardenper100g',
        'naringsvarde',
        'nutritionalclaim',
        'nutritionalinfo',
        'nutrition',
        'energi',
        'energy',
        'fett',
        'fat',
        'mattatfett',
        'kolhydrat',
        'carbohydrate',
        'sockerarter',
        'protein',
        'salt',
      ],
    },
  ],
  wine: [
    {
      id: 'wine-basics',
      title: 'Vininfo',
      aliases: [
        'produktnamn',
        'produktnamnvinid',
        'artikelnummer',
        'artikelnummervinid',
        'producent',
        'producentvinid',
        'producer',
        'volym',
        'volymvinid',
        'volume',
        'ursprungsland',
        'countryoforigin',
      ],
    },
    {
      id: 'wine-description',
      title: 'Beskrivning',
      aliases: [
        'beskrivning',
        'description',
        'beskrivningvingard',
        'beskrivningvingardvinid',
        'vingard',
        'vingardvinid',
        'vineyard',
      ],
    },
    {
      id: 'wine-systembolaget',
      title: 'Systembolaget',
      aliases: [
        'bestallningsnummersystembolaget',
        'bestallningsnummersystembolagetvinid',
        'systembolagetbestallningsnummer',
        'systembolaget',
      ],
    },
  ],
  recipes: [
    {
      id: 'recipe-basics',
      title: 'Receptinformation',
      aliases: [
        'receptnamn',
        'receptnamnreceptid',
        'recipename',
        'beskrivning',
        'description',
        'tillagningstid',
        'tillagningstidreceptid',
        'cookingtime',
        'cooktime',
        'antalportioner',
        'antalportionerreceptid',
        'portioner',
        'servings',
      ],
    },
    {
      id: 'recipe-ingredients',
      title: 'Ingredienser',
      aliases: ['ingredienser', 'ingredients'],
    },
    {
      id: 'recipe-instructions',
      title: 'Instruktioner',
      aliases: ['instruktioner', 'instruktionerreceptid', 'instructions'],
    },
  ],
};

const isPublicEmptyValue = (raw: any) =>
  raw === undefined ||
  raw === null ||
  (typeof raw === 'string' && raw.trim() === '') ||
  (Array.isArray(raw) && raw.length === 0);

const getMetadataValueForField = (metadata: Record<string, any> | undefined, field: any) => (
  metadata?.[field.id] ?? metadata?.[field.name] ?? metadata?.[field.label]
);

const formatPlainMetadataValue = (raw: any): string => {
  if (isPublicEmptyValue(raw)) return '';
  if (Array.isArray(raw)) return raw.map((value) => String(value)).filter(Boolean).join(', ');
  if (typeof raw === 'object') return formatAssetReferenceLabels(raw);
  return String(raw);
};

const fieldMatchesAliases = (
  field: { id?: string; name?: string; label?: string },
  aliases: string[],
): boolean => {
  const checks = [field.id, field.name, field.label]
    .map((value) => normalizePublicShareText(value))
    .filter(Boolean);
  return checks.some((fieldText) => aliases.some((alias) => fieldTextMatchesAlias(fieldText, alias)));
};

const getPublicMetadataSectionId = (
  field: { id?: string; name?: string; label?: string },
  profile: PublicMetadataProfile | null,
): string => {
  if (!profile) return 'other';
  const section = PUBLIC_METADATA_SECTION_ALIASES[profile].find((candidate) => (
    fieldMatchesAliases(field, candidate.aliases)
  ));
  return section?.id || 'other';
};

const PublicSharePage: React.FC<PublicSharePageProps> = ({ shareToken }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [collection, setCollection] = useState<
    | { _id: string; name: string; description?: string; assets: string[] }
    | null
  >(null);
  const [allowDownload, setAllowDownload] = useState(true);
  const [customMessage, setCustomMessage] = useState<string | undefined>();
  const [includeMetadata, setIncludeMetadata] = useState(false);
  const [assets, setAssets] = useState<PublicAsset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<PublicAsset | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [banner, setBanner] = useState<{ enabled?: boolean; imagePath?: string; height?: number; focalY?: number; } | undefined>();
  const [logoExists, setLogoExists] = useState(false);
  const [accessKey, setAccessKey] = useState<string | null>(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [downloadMenuAsset, setDownloadMenuAsset] = useState<PublicAsset | null>(null);
  const [downloadingAssetId, setDownloadingAssetId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<PublicAssetFilter>('all');
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const shareBaseUrl = useMemo(() => `${API_BASE_URL}/api/share/public/${shareToken}`, [shareToken]);

  useEffect(() => {
    // Check if logo exists
    const checkLogo = async () => {
      try {
        await axios.head(`${API_BASE_URL}/logo.png`);
        setLogoExists(true);
      } catch (e) {
        setLogoExists(false);
      }
    };
    checkLogo();

    // Load templates for label/order/section rendering
    const loadTemplates = async () => {
      try {
        const resp = await axios.get<ITemplate[]>(`${API_BASE_URL}/api/templates`);
        setTemplates(Array.isArray(resp.data) ? resp.data : []);
      } catch {}
    };
    loadTemplates();

    const init = async () => {
      try {
        setLoading(true);
        setError(null);
        const resp = await axios.get<PublicCollectionResponse>(shareBaseUrl);
        if ((resp.data as any).requiresPassword) {
          const data = resp.data as Extract<PublicCollectionResponse, { requiresPassword: true }>;
          setRequiresPassword(true);
          setCustomMessage(data.customMessage);
          setBanner(data.banner);
        } else {
          const data = resp.data as Extract<PublicCollectionResponse, { requiresPassword: false }>;
          setRequiresPassword(false);
          setCollection(data.collection);
          setAllowDownload(!!data.allowDownload);
          setCustomMessage(data.settings?.customMessage);
          setIncludeMetadata(!!data.settings?.includeMetadata);
          setBanner(data.settings?.banner);
          if (data.collection.assets?.length) {
            await fetchAssets(data.collection.assets, !!data.settings?.includeMetadata);
          }
        }
      } catch (e: any) {
        setError(e?.response?.data?.message || e?.message || 'Kunde inte hämta delad samling');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [shareBaseUrl]);

  // Find matching template for an asset using the same logic as CollectionDetailView
  const findTemplateForAsset = (asset: PublicAsset): ITemplate | null => {
    if (!asset?.categories || !asset.categories.length || !templates.length) return null;
    const assetCatIds = asset.categories.map(String);
    const match = templates.find(tpl => tpl.categoryIds.some(catId => assetCatIds.includes(String(catId))));
    if (match) return match;
    const defTpl = templates.find(tpl => tpl.isDefault);
    return defTpl || null;
  };

  const decodeFileName = (asset: PublicAsset | null): string => {
    if (!asset?.fileName) return '';
    try {
      return decodeURIComponent(asset.fileName.replace(/\+/g, ' '));
    } catch {
      return asset.fileName;
    }
  };

  const formatFileTypeLabel = (fileType?: string): string => {
    if (!fileType) return '';
    const subtype = fileType.split('/')[1] || fileType;
    return subtype.toUpperCase();
  };

  const getAssetDisplayTitle = (asset: PublicAsset | null): string => {
    if (!asset) return 'Produktinformation';
    const tpl = findTemplateForAsset(asset) || (asset.template as any);
    const profile = getMetadataProfileForAsset(asset, tpl);
    const titleAliases = profile ? PUBLIC_METADATA_TITLE_ALIASES[profile] : [];

    if (tpl?.fields && titleAliases.length > 0) {
      const titleField = (tpl.fields as any[]).find((field) => fieldMatchesAliases(field, titleAliases));
      const titleValue = titleField ? formatPlainMetadataValue(getMetadataValueForField(asset.metadata, titleField)) : '';
      if (titleValue) return titleValue;
    }

    if (titleAliases.length > 0 && asset.metadata) {
      for (const [key, value] of Object.entries(asset.metadata)) {
        if (titleAliases.some((alias) => fieldTextMatchesAlias(normalizePublicShareText(key), alias))) {
          const titleValue = formatPlainMetadataValue(value);
          if (titleValue) return titleValue;
        }
      }
    }

    return decodeFileName(asset) || 'Produktinformation';
  };

  const assetMatchesFilter = (asset: PublicAsset, filter: PublicAssetFilter): boolean => {
    if (filter === 'all') return true;
    return assetMatchesFilterDefinition(asset, filter);
  };

  const renderPublicMetadataSections = (
    fields: Array<{
      key: string;
      label: string;
      raw: any;
      displayValue: string;
      displayHtml: string;
      hideFieldLabel?: boolean;
      spanFullWidth?: boolean;
      sectionId: string;
    }>,
    profile: PublicMetadataProfile | null,
  ) => {
    const sectionDefinitions = profile ? PUBLIC_METADATA_SECTION_ALIASES[profile] : [];
    const orderedSections = [
      ...sectionDefinitions,
      { id: 'other', title: 'Övrig information', aliases: [] },
    ]
      .map((section) => ({
        ...section,
        fields: fields.filter((field) => field.sectionId === section.id),
      }))
      .filter((section) => section.fields.length > 0);

    if (orderedSections.length === 0) return null;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        {orderedSections.map((section) => (
          <Paper
            key={section.id}
            elevation={0}
            sx={{
              border: '1px solid #e5eaf0',
              borderRadius: '12px',
              overflow: 'hidden',
              bgcolor: '#fff',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.25,
                bgcolor: '#f8fafc',
                borderBottom: '1px solid #e5eaf0',
              }}
            >
              <Typography sx={{ fontWeight: 700, color: '#14294a', fontSize: '0.92rem' }}>
                {section.title}
              </Typography>
            </Box>
            <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 3.5 }}>
              {section.fields.map((field, index) => (
                <Box
                  key={field.key}
                  sx={{
                    gridColumn: field.spanFullWidth ? '1 / -1' : undefined,
                    minWidth: 0,
                    py: 1.15,
                    borderTop: index === 0 ? 0 : '1px solid #edf1f5',
                    '&:nth-of-type(1), &:nth-of-type(2)': {
                      borderTop: { md: 0 },
                    },
                  }}
                >
                  {!field.hideFieldLabel && (
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.04em' }}>
                      {field.label}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      typography: 'body2',
                      mt: field.hideFieldLabel ? 0 : 0.35,
                      color: 'text.primary',
                      lineHeight: 1.55,
                      whiteSpace: field.displayHtml ? undefined : 'pre-wrap',
                      '& b, & strong': { fontWeight: 700 },
                    }}
                    {...(field.displayHtml ? { dangerouslySetInnerHTML: { __html: field.displayHtml } } : {})}
                  >
                    {!field.displayHtml ? field.displayValue : null}
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        ))}
      </Box>
    );
  };

  const fetchAssets = async (assetIds: string[], withMetadata: boolean = false, shareAccessKey?: string | null) => {
    try {
      const url = `${API_BASE_URL}/api/assets/public`;
      // Fetch in chunks to avoid URL length limits with large collections
      const CHUNK_SIZE = 50;
      const allAssets: PublicAsset[] = [];
      for (let i = 0; i < assetIds.length; i += CHUNK_SIZE) {
        const chunk = assetIds.slice(i, i + CHUNK_SIZE);
        const resp = await axios.get<PublicAsset[]>(url, {
          params: { 
            assetIds: chunk.join(','), 
            shareToken,
            includeMetadata: withMetadata ? 'true' : 'false',
            ...(shareAccessKey ? { accessKey: shareAccessKey } : {}),
          }
        });
        if (resp.data) {
          allAssets.push(...resp.data);
        }
      }
      setAssets(allAssets);
      setSelectedAssetIds((current) => current.filter((assetId) => allAssets.some((asset) => asset._id === assetId)));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Kunde inte hämta assets');
    }
  };

  const handleVerifyPassword = async () => {
    try {
      setVerifying(true);
      setError(null);
      const verifyUrl = `${shareBaseUrl}/verify`;
      const resp = await axios.post(verifyUrl, { password });
      // Successful verification returns full collection and settings
      const data = resp.data as any;
      const col = data.collection as { _id: string; name: string; description?: string; assets: string[] };
      setCollection(col);
      setAllowDownload(!!data.allowDownload);
      setCustomMessage(data.settings?.customMessage);
      setIncludeMetadata(!!data.settings?.includeMetadata);
      setBanner(data.settings?.banner);
      if (data.accessKey) setAccessKey(data.accessKey);
      setRequiresPassword(false);
      if (col.assets?.length) {
        await fetchAssets(col.assets, !!data.settings?.includeMetadata, data.accessKey);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Fel lösenord');
    } finally {
      setVerifying(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
  };

  const getFileIcon = (fileType: string) => {
    if (fileType?.startsWith('image/')) return <ImageIcon sx={{ fontSize: 40 }} />;
    return <InsertDriveFileIcon sx={{ fontSize: 40 }} />;
  };

  const buildDownloadParams = () => {
    const params = new URLSearchParams({ shareToken });
    if (accessKey) params.set('accessKey', accessKey);
    return params;
  };

  const handleDownloadAllAsZip = () => {
    const params = buildDownloadParams();
    const url = `${shareBaseUrl}/download?${params.toString()}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${collection?.name || 'collection'}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownloadSelectedAsZip = () => {
    if (selectedAssetIds.length === 0) {
      setSnackbar({ open: true, message: 'Välj minst en fil att ladda ner', severity: 'error' });
      return;
    }

    const params = buildDownloadParams();
    params.set('assetIds', selectedAssetIds.join(','));
    const url = `${shareBaseUrl}/download?${params.toString()}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${collection?.name || 'collection'}-valda.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const toggleAssetSelection = (assetId: string) => {
    setSelectedAssetIds((current) => (
      current.includes(assetId)
        ? current.filter((id) => id !== assetId)
        : [...current, assetId]
    ));
  };

  const handleDownloadAsset = async (asset: PublicAsset, format: string = 'original') => {
    setDownloadingAssetId(asset._id);
    setDownloadMenuAnchor(null);
    try {
      const params = buildDownloadParams();
      if (format !== 'original') params.set('format', format);
      const url = `${API_BASE_URL}/api/assets/${asset._id}/download/public?${params.toString()}`;

      const resp = await fetch(url);
      if (!resp.ok) {
        let msg = 'Nedladdningen misslyckades';
        try {
          const body = await resp.json();
          if (body.message) msg = body.message;
        } catch {}
        setSnackbar({ open: true, message: msg, severity: 'error' });
        return;
      }

      const blob = await resp.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;

      let fileName = asset.fileName || 'download';
      try { fileName = decodeURIComponent(fileName.replace(/\+/g, ' ')); } catch {}
      if (format === 'web') fileName = fileName.replace(/\.[^.]+$/, '_web$&');
      else if (format === 'transparent') fileName = fileName.replace(/\.[^.]+$/, '_transparent.png');
      else if (format === 'illustration') fileName = fileName.replace(/\.[^.]+$/, '_illustration.png');

      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Nedladdningen misslyckades', severity: 'error' });
    } finally {
      setDownloadingAssetId(null);
    }
  };

  const openDownloadMenu = (event: React.MouseEvent<HTMLElement>, asset: PublicAsset) => {
    const isImage = asset.fileType?.startsWith('image/');
    const isVector =
      asset.fileType?.includes('ai') || asset.fileType?.includes('eps') ||
      asset.fileType?.includes('svg') || asset.fileType?.includes('pdf') ||
      asset.fileType === 'application/postscript' || asset.fileType === 'application/illustrator' ||
      asset.fileName?.toLowerCase().match(/\.(ai|eps|svg|pdf)$/);

    if (!isImage && !isVector) {
      handleDownloadAsset(asset, 'original');
      return;
    }
    setDownloadMenuAsset(asset);
    setDownloadMenuAnchor(event.currentTarget);
  };

  const getFormatOptions = (asset: PublicAsset) => {
    const options: { id: string; label: string; icon: React.ReactNode }[] = [
      { id: 'original', label: 'Originalbild', icon: <ImageIcon fontSize="small" /> },
    ];
    const isImage = asset.fileType?.startsWith('image/');
    const isVector =
      asset.fileType?.includes('ai') || asset.fileType?.includes('eps') ||
      asset.fileType?.includes('svg') || asset.fileType?.includes('pdf') ||
      asset.fileType === 'application/postscript' || asset.fileType === 'application/illustrator' ||
      asset.fileName?.toLowerCase().match(/\.(ai|eps|svg|pdf)$/);
    if (isImage) {
      options.push({ id: 'web', label: 'Webbild (max 1920px)', icon: <DesktopWindowsIcon fontSize="small" /> });
      options.push({ id: 'transparent', label: 'Transparent PNG', icon: <CropOriginalIcon fontSize="small" /> });
    }
    if (isVector) {
      options.push({ id: 'illustration', label: 'Illustration (PNG)', icon: <BrushIcon fontSize="small" /> });
    }
    return options;
  };

  const availableAssetFilters = PUBLIC_ASSET_FILTERS.filter((filter) => (
    assets.some((asset) => assetMatchesFilter(asset, filter.id))
  ));

  const visibleAssets = assets.filter((asset) => assetMatchesFilter(asset, activeFilter));

  const selectedAssets = assets.filter((asset) => selectedAssetIds.includes(asset._id));

  const allVisibleAssetsSelected = visibleAssets.length > 0
    && visibleAssets.every((asset) => selectedAssetIds.includes(asset._id));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Blue header with logo, title and download button */}
      <AppBar position="static" sx={{ bgcolor: '#14294a' }}>
        <Toolbar sx={{ py: 2 }}>
          <Box
            component="img"
            src={logoExists ? `${API_BASE_URL}/logo.png` : "/favicon-512.png"}
            alt="Logo"
            sx={{ height: 40, mr: 2 }}
          />
          <Typography variant="h5" component="div" sx={{ flexGrow: 1, color: 'white', fontWeight: 'bold' }}>
            {collection?.name || 'Delad samling'}
          </Typography>
          {!requiresPassword && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <Tooltip title={allowDownload ? 'Ladda ner alla som ZIP' : 'Nedladdning är avstängd'}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<FolderZipIcon />}
                    onClick={handleDownloadAllAsZip}
                    disabled={!allowDownload || !assets.length}
                    sx={{ bgcolor: 'white', color: '#14294a', '&:hover': { bgcolor: '#f5f5f5' } }}
                  >
                    Ladda ner alla
                  </Button>
                </span>
              </Tooltip>
              <Typography variant="caption" sx={{ color: 'white', opacity: 0.8, mt: 0.5 }}>
                {assets.length} {assets.length === 1 ? 'fil' : 'filer'}
              </Typography>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Banner image if enabled - constrained to container width */}
      {banner?.enabled && banner.imagePath && (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Box
            sx={{
              width: '100%',
              height: `${banner.height || 300}px`,
              backgroundImage: `url(${API_BASE_URL}${banner.imagePath})`,
              backgroundSize: 'cover',
              backgroundPosition: `center ${banner.focalY || 50}%`,
              borderRadius: 1
            }}
          />
        </Container>
      )}

      <Container maxWidth="lg" sx={{ mt: banner?.enabled && banner.imagePath ? 3 : 4, mb: 6 }}>
        {!!error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {requiresPassword ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>Lösenord krävs</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Den här delningen är skyddad med lösenord.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              type="password"
              label="Lösenord"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              size="small"
            />
            <Button variant="contained" onClick={handleVerifyPassword} disabled={!password || verifying}>
              {verifying ? 'Verifierar…' : 'Öppna'}
            </Button>
          </Box>
        </Paper>
      ) : (
        <>
          {/* Description box - only show if there's a non-empty custom message */}
          {customMessage && customMessage.trim() && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="body1" color="text.secondary">
                {customMessage}
              </Typography>
            </Paper>
          )}

          {!assets.length ? (
            <Alert severity="info">Inga filer i denna samling.</Alert>
          ) : (
            <>
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  mb: 2,
                  p: 2,
                  border: '1px solid #e5eaf0',
                  borderRadius: '12px',
                  bgcolor: '#fff',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {activeFilter === 'all'
                        ? `${assets.length} ${assets.length === 1 ? 'fil i samlingen' : 'filer i samlingen'}`
                        : `${visibleAssets.length} av ${assets.length} filer visas`}
                    </Typography>
                    {includeMetadata && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Klicka på ett kort för att se produktinformation
                      </Typography>
                    )}
                  </Box>
                  {allowDownload && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {selectedAssets.length} valda
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setSelectedAssetIds((current) => {
                            const visibleIds = visibleAssets.map((asset) => asset._id);
                            if (allVisibleAssetsSelected) {
                              return current.filter((assetId) => !visibleIds.includes(assetId));
                            }
                            return Array.from(new Set([...current, ...visibleIds]));
                          });
                        }}
                        disabled={!visibleAssets.length}
                        sx={{ textTransform: 'none' }}
                      >
                        {allVisibleAssetsSelected ? 'Avmarkera visade' : 'Markera visade'}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<FolderZipIcon />}
                        onClick={handleDownloadSelectedAsZip}
                        disabled={selectedAssets.length === 0}
                        sx={{ bgcolor: '#14294a', textTransform: 'none', '&:hover': { bgcolor: '#0f203a' } }}
                      >
                        Ladda ner valda
                      </Button>
                      {selectedAssets.length > 0 && (
                        <Button
                          size="small"
                          onClick={() => setSelectedAssetIds([])}
                          sx={{ textTransform: 'none', color: 'text.secondary' }}
                        >
                          Rensa
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
                {availableAssetFilters.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label="Alla"
                      clickable
                      color={activeFilter === 'all' ? 'primary' : 'default'}
                      variant={activeFilter === 'all' ? 'filled' : 'outlined'}
                      onClick={() => setActiveFilter('all')}
                      sx={{ fontWeight: 600 }}
                    />
                    {availableAssetFilters.map((filter) => (
                      <Chip
                        key={filter.id}
                        label={filter.label}
                        clickable
                        color={activeFilter === filter.id ? 'primary' : 'default'}
                        variant={activeFilter === filter.id ? 'filled' : 'outlined'}
                        onClick={() => setActiveFilter(filter.id)}
                        sx={{ fontWeight: 600 }}
                      />
                    ))}
                  </Box>
                )}
              </Paper>
              {visibleAssets.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>Inga filer matchar valt filter.</Alert>
              ) : null}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {visibleAssets.map((asset) => {
                const tpl = findTemplateForAsset(asset) || (asset.template as any);
                const profile = getMetadataProfileForAsset(asset, tpl);
                const chipLabel = getProfileChipLabel(profile);
                const displayTitle = getAssetDisplayTitle(asset);
                const decodedFileName = decodeFileName(asset);
                const showSecondaryFileName = !!decodedFileName && decodedFileName !== displayTitle;
                const isClickable = includeMetadata;
                const isImage = asset.fileType?.startsWith('image/');
                const isSelected = selectedAssetIds.includes(asset._id);
                const openDetails = () => {
                  if (isClickable) {
                    setSelectedAsset(asset);
                    setDetailDialogOpen(true);
                  }
                };
                return (
                <Box key={asset._id} sx={{ width: { xs: '100%', sm: 'calc(50% - 24px)', md: 'calc(33.33% - 24px)', lg: 'calc(25% - 24px)' } }}>
                  <Card
                    sx={{
                      height: '100%',
                      minHeight: 320,
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: isClickable ? 'pointer' : 'default',
                      border: '1px solid #e5eaf0',
                      borderRadius: '12px',
                      boxShadow: 'none',
                      overflow: 'hidden',
                      position: 'relative',
                      transition: 'box-shadow 200ms ease, transform 200ms ease, border-color 200ms ease',
                      ...(isSelected ? { borderColor: '#14294a', boxShadow: '0 0 0 2px rgba(20, 41, 74, 0.12)' } : {}),
                      '&:hover': isClickable
                        ? {
                            boxShadow: '0 8px 24px rgba(20, 41, 74, 0.10)',
                            borderColor: '#cfd6e0',
                            transform: 'translateY(-2px)',
                            '& .public-card-image': { transform: 'scale(1.03)' },
                          }
                        : undefined,
                    }}
                    onClick={openDetails}
                  >
                    <CardMedia
                      component="div"
                      sx={{
                        height: 186,
                        backgroundColor: '#f7f8fa',
                        borderBottom: '1px solid #eef1f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        p: 1.5,
                      }}
                    >
                      {allowDownload && (
                        <Checkbox
                          checked={isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleAssetSelection(asset._id)}
                          inputProps={{ 'aria-label': `Markera ${displayTitle}` }}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            zIndex: 1,
                            bgcolor: 'rgba(255,255,255,0.88)',
                            borderRadius: '8px',
                            p: 0.35,
                            color: '#14294a',
                            '&:hover': { bgcolor: '#fff' },
                          }}
                        />
                      )}
                      {isImage && publicMediumSrc(asset) ? (
                        <img
                          className="public-card-image"
                          src={publicMediumSrc(asset) || undefined}
                          alt={displayTitle}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            transition: 'transform 250ms ease',
                          }}
                        />
                      ) : (
                        getFileIcon(asset.fileType)
                      )}
                    </CardMedia>
                    <CardContent sx={{ flexGrow: 1, pb: 1.25 }}>
                      {chipLabel && (
                        <Chip
                          label={chipLabel}
                          size="small"
                          sx={{
                            mb: 1,
                            height: 22,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            bgcolor: '#eef2f7',
                            color: '#14294a',
                          }}
                        />
                      )}
                      <Typography
                        variant="subtitle1"
                        component="h2"
                        title={displayTitle}
                        sx={{
                          fontSize: '0.98rem',
                          fontWeight: 700,
                          lineHeight: 1.3,
                          color: '#14294a',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-word',
                        }}
                      >
                        {displayTitle}
                      </Typography>
                      {showSecondaryFileName && (
                        <Typography
                          variant="caption"
                          noWrap
                          title={decodedFileName}
                          sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}
                        >
                          {decodedFileName}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
                        {[formatFileSize(asset.fileSize), formatFileTypeLabel(asset.fileType)].filter(Boolean).join(' · ')}
                      </Typography>
                    </CardContent>
                    <CardActions
                      sx={{
                        justifyContent: 'space-between',
                        px: 2,
                        pt: 0,
                        pb: 1.5,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {includeMetadata ? (
                        <Button
                          size="small"
                          startIcon={<InfoIcon fontSize="small" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedAsset(asset);
                            setDetailDialogOpen(true);
                          }}
                          sx={{
                            textTransform: 'none',
                            color: '#14294a',
                            fontWeight: 600,
                            px: 0.5,
                            minWidth: 'auto',
                            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                          }}
                        >
                          Visa info
                        </Button>
                      ) : (
                        <span />
                      )}
                      <Tooltip title={allowDownload ? 'Ladda ner' : 'Nedladdning är avstängd'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDownloadMenu(e, asset);
                            }}
                            disabled={!allowDownload || downloadingAssetId === asset._id}
                            sx={{
                              color: '#14294a',
                              border: '1px solid #e5eaf0',
                              borderRadius: '8px',
                              '&:hover': { bgcolor: '#f3f5f9', borderColor: '#cfd6e0' },
                            }}
                          >
                            {downloadingAssetId === asset._id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DownloadIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Box>
                );
              })}
              </Box>
            </>
          )}
        </>
      )}
      </Container>

      {/* Asset Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2933', lineHeight: 1.25 }}>
                {getAssetDisplayTitle(selectedAsset)}
              </Typography>
              {selectedAsset && (
                <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary', wordBreak: 'break-word' }}>
                  {[decodeFileName(selectedAsset), formatFileTypeLabel(selectedAsset.fileType), formatFileSize(selectedAsset.fileSize)]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              )}
            </Box>
            <IconButton onClick={() => setDetailDialogOpen(false)} sx={{ mt: -0.5, color: 'text.secondary' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 2.5, sm: 3 }, py: 3 }}>
          {selectedAsset && (
            <Box>
              {/* Image preview */}
              {selectedAsset.fileType?.startsWith('image/') && (
                <Box
                  sx={{
                    mb: 3.5,
                    p: { xs: 2, sm: 3 },
                    minHeight: { xs: 220, sm: 300 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#f7f8fa',
                    border: '1px solid #edf0f2',
                    borderRadius: '12px',
                  }}
                >
                  {publicMediumSrc(selectedAsset) ? (
                    <img
                      src={publicMediumSrc(selectedAsset) || undefined}
                      alt={selectedAsset.fileName}
                      style={{ maxWidth: '100%', maxHeight: 360, objectFit: 'contain' }}
                    />
                  ) : (
                    getFileIcon(selectedAsset.fileType)
                  )}
                </Box>
              )}

              {/* Metadata / Produktinformation */}
              {selectedAsset.metadata && Object.keys(selectedAsset.metadata).length > 0 && (
                <>
                  {(() => {
                    // Prefer dynamic template resolution by categories; fallback to embedded template; else show raw metadata
                    const tpl = findTemplateForAsset(selectedAsset) || (selectedAsset.template as any);
                    const metadataProfile = getMetadataProfileForAsset(selectedAsset, tpl);
                    if (tpl && (tpl as any).fields) {
                      const fields = (tpl as any).fields as any[];
                      const sortedFields = fields
                        .filter((field: any) => field.showDuringEdit !== false && isFieldAllowedForProfile(field, metadataProfile))
                        .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
                      const seen = new Set<string>();
                      const isEmptyValue = (raw: any) =>
                        raw === undefined ||
                        raw === null ||
                        (typeof raw === 'string' && raw.trim() === '') ||
                        (Array.isArray(raw) && raw.length === 0);
                      const getRawValue = (field: any) =>
                        selectedAsset.metadata?.[field.id] ?? selectedAsset.metadata?.[field.name] ?? selectedAsset.metadata?.[field.label];
                      const formatDisplayValue = (field: any, raw: any) => {
                        if (isEmptyValue(raw)) return '—';
                        if (field.type === 'checkbox') return raw === true ? 'Ja' : raw === false ? 'Nej' : String(raw);
                        if (field.type === 'reference') return formatAssetReferenceLabels(raw);
                        if (field.type === 'tags') return Array.isArray(raw) ? raw.join(', ') : String(raw);
                        if (field.type === 'list') {
                          const values = Array.isArray(raw)
                            ? raw
                            : typeof raw === 'string'
                              ? raw.split(',').map((value: string) => value.trim()).filter(Boolean)
                              : [raw];
                          if (values.length === 0) return '—';
                          return values
                            .map((value: any) => field.options?.find((option: any) => option.value === String(value))?.label || String(value))
                            .join(', ');
                        }
                        if (field.type === 'date') {
                          try {
                            const d = raw instanceof Date ? raw : new Date(raw);
                            return d.toLocaleDateString('sv-SE');
                          } catch {
                            return String(raw);
                          }
                        }
                        return String(raw);
                      };
                      const getDisplayHtml = (field: any, raw: any) => (
                        shouldRenderSimpleTemplateHtml(field?.type, raw)
                          ? sanitizeSimpleTemplateHtml(raw)
                          : ''
                      );
                      const shouldSpanFullWidth = (field: any, raw: any, displayValue: string, displayHtml: string) => {
                        if (Array.isArray(raw)) return raw.length > 1;
                        if (field.type === 'textarea' || field.type === 'reference') return true;
                        const textLength = (displayHtml || displayValue || '').replace(/<[^>]+>/g, '').length;
                        return textLength > 60;
                      };
                      const renderedFields = sortedFields
                        .map((field: any, idx: number) => {
                              if (field.type === 'separator') {
                                return null;
                              }
                              const raw = getRawValue(field);
                              if (isEmptyValue(raw)) return null;
                              const lNorm = normalizePublicShareText(field.label || field.name || field.id);
                              if (seen.has(lNorm)) return null; seen.add(lNorm);
                              const displayValue = formatDisplayValue(field, raw);
                              const displayHtml = getDisplayHtml(field, raw);
                              const hideFieldLabel = field.hideLabelInDisplay === true;
                              const spanFullWidth = shouldSpanFullWidth(field, raw, displayValue, displayHtml);
                              return {
                                key: field.id || field.name || String(idx),
                                label: field.label || field.name || field.id,
                                raw,
                                displayValue,
                                displayHtml,
                                hideFieldLabel,
                                spanFullWidth,
                                sectionId: getPublicMetadataSectionId(field, metadataProfile),
                              };
                            })
                        .filter(Boolean) as Array<{
                          key: string;
                          label: string;
                          raw: any;
                          displayValue: string;
                          displayHtml: string;
                          hideFieldLabel?: boolean;
                          spanFullWidth?: boolean;
                          sectionId: string;
                        }>;

                      return renderPublicMetadataSections(renderedFields, metadataProfile);
                    }
                    // No template available – show raw key/value pairs (filtered by whitelist)
                    const fallbackFields = Object.entries(selectedAsset.metadata)
                      .map(([key, value]) => {
                          if (key.startsWith('_') || key === '$') return null;
                          if (!isFieldAllowedForProfile({ id: key, name: key, label: key }, metadataProfile)) return null;
                          if (isPublicEmptyValue(value)) return null;
                          const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
                          const formattedValue = formatPlainMetadataValue(value);
                          const spanFullWidth = formattedValue.length > 60 || Array.isArray(value);
                          const fieldRef = { id: key, name: key, label: key };
                          return {
                            key,
                            label: formattedKey,
                            raw: value,
                            displayValue: formattedValue,
                            displayHtml: '',
                            spanFullWidth,
                            sectionId: getPublicMetadataSectionId(fieldRef, metadataProfile),
                          };
                        })
                      .filter(Boolean) as Array<{
                        key: string;
                        label: string;
                        raw: any;
                        displayValue: string;
                        displayHtml: string;
                        spanFullWidth?: boolean;
                        sectionId: string;
                      }>;

                    return renderPublicMetadataSections(fallbackFields, metadataProfile);
                  })()}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, py: 2, borderTop: '1px solid #edf0f2' }}>
          <Button
            onClick={() => setDetailDialogOpen(false)}
            sx={{ minHeight: 40, px: 2, textTransform: 'none', color: '#475569' }}
          >
            Stäng
          </Button>
          {selectedAsset && allowDownload && (
            <Button
              variant="contained"
              startIcon={downloadingAssetId === selectedAsset._id ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              disabled={downloadingAssetId === selectedAsset._id}
              onClick={(e) => {
                openDownloadMenu(e, selectedAsset);
              }}
              sx={{
                minHeight: 40,
                px: 2.5,
                textTransform: 'none',
                bgcolor: '#14294a',
                '&:hover': { bgcolor: '#0f203a' },
              }}
            >
              Ladda ner
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Download format menu */}
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor) && Boolean(downloadMenuAsset)}
        onClose={() => { setDownloadMenuAnchor(null); setDownloadMenuAsset(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {downloadMenuAsset && getFormatOptions(downloadMenuAsset).map((opt) => (
          <MenuItem
            key={opt.id}
            onClick={() => {
              if (downloadMenuAsset) handleDownloadAsset(downloadMenuAsset, opt.id);
              setDownloadMenuAsset(null);
            }}
            disabled={!!downloadingAssetId}
          >
            <ListItemIcon>{opt.icon}</ListItemIcon>
            <ListItemText>{opt.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Download feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PublicSharePage;


